import {
  fetchOrderDeliveryDetails,
  type DashboardExperienceOrderDeliveryDetailsResponse,
} from '@library/api-dashboard-experience'
import { datadogLogs } from '@datadog/browser-logs'
import { datadogRum } from '@datadog/browser-rum'
import useSWR from 'swr'

type UseOrderDeliveryDetailsResult = {
  orderDeliveryDetails: DashboardExperienceOrderDeliveryDetailsResponse | undefined
  error: Error | undefined
  isLoading: boolean
}

const initialState: UseOrderDeliveryDetailsResult = {
  orderDeliveryDetails: undefined,
  error: undefined,
  isLoading: false,
}

const getStatusCode = (error: unknown): number | undefined => {
  if (typeof error === 'object' && error !== null && 'status' in error) {
    const status = (error as { status?: unknown }).status

    if (typeof status === 'number') {
      return status
    }
  }

  if (typeof error === 'object' && error !== null && 'message' in error) {
    const message = (error as { message?: unknown }).message

    if (typeof message === 'string') {
      const statusMatch = message.match(/status=(\d+)/)

      if (statusMatch) {
        return Number(statusMatch[1])
      }
    }
  }

  return undefined
}

const useOrderDeliveryDetails = (
  orderId?: string,
  enabled: boolean = true,
): UseOrderDeliveryDetailsResult => {
  const swrKey = orderId && enabled ? orderId : null
  const { data, error } = useSWR(swrKey, fetchOrderDeliveryDetails)

  if (!orderId || !enabled) {
    return initialState
  }

  if (error) {
    const errorMessage = `Error fetching order delivery details in myHome ${JSON.stringify(error)}`
    const statusCode = getStatusCode(error)

    datadogLogs.logger.error(errorMessage)
    datadogRum.addError(error, {
      page: 'MyHomePage',
      errorMessage: errorMessage,
    })
    if (statusCode && statusCode !== 200) {
      datadogRum.addAction('order_delivery_details_api_error', {
        page: 'MyHomePage',
        statusCode,
      })
    }

    return {
      ...initialState,
      error,
      isLoading: false,
    }
  }

  if (!data) {
    return {
      ...initialState,
      isLoading: true,
    }
  }

  return {
    orderDeliveryDetails: data,
    error,
    isLoading: false,
  }
}

export { useOrderDeliveryDetails }
